import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Clock, Send, Trash2, XCircle, Pencil } from 'lucide-react';
import { getSocialApiBaseUrl } from '../../../utils/socialApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import { gymSocialBtnPrimary, gymSocialBtnSecondary, gymSocialMutedText, gymSocialTitleText } from '../gymPageTheme';
import { contentTypeBadgeClass, contentTypeI18nKey } from './socialContentTypes';
import GymSocialMediaThumb from './GymSocialMediaThumb';
import SocialTargetBadges from './SocialTargetBadges';
import { mediaItemUrl } from './socialMediaUtils';

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

const POLL_INTERVAL_MS = 30000;
const SCHEDULED_POLL_MARGIN_MS = 8 * 60 * 1000;

function postNeedsPolling(post) {
  if (post.status === 'publishing') {
    return true;
  }
  if (post.status !== 'scheduled' || !post.scheduled_at) {
    return false;
  }
  const scheduledMs = new Date(String(post.scheduled_at).replace(' ', 'T')).getTime();
  if (Number.isNaN(scheduledMs)) {
    return true;
  }
  return Date.now() < scheduledMs + SCHEDULED_POLL_MARGIN_MS;
}

export default function GymSocialPostsList({ api, refreshKey = 0, onChanged, onPollingChange, onEditPost }) {
  const { t } = useTranslation();
  const socialApi = getSocialApiBaseUrl();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const postsSigRef = useRef('');

  const load = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) {
        setLoading(true);
      }
      try {
        const res = await api.get('gym_social_posts.php?action=list', {
          baseURL: socialApi,
          params: { limit: 30 },
        });
        const next = res.data.posts || [];
        const sig = JSON.stringify(next.map((p) => [p.id, p.status, p.publish_error]));
        if (silent && sig !== postsSigRef.current) {
          onChanged?.();
        }
        postsSigRef.current = sig;
        setPosts(next);
        onPollingChange?.(next.some(postNeedsPolling));
      } catch {
        setPosts([]);
        postsSigRef.current = '';
        onPollingChange?.(false);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [api, socialApi, onChanged, onPollingChange]
  );

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!posts.some(postNeedsPolling)) {
      return undefined;
    }
    const id = window.setInterval(() => load({ silent: true }), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [posts, load]);

  const runAction = async (action, body, successKey) => {
    setBusyId(body.id);
    try {
      await api.post(`gym_social_posts.php?action=${action}`, body, { baseURL: socialApi });
      toast.success(t(successKey));
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.posts.saveError'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (posts.length === 0) {
    return <p className={`text-sm py-4 ${gymSocialMutedText}`}>{t('gym.social.posts.emptyList')}</p>;
  }

  return (
    <ul className="space-y-2">
      {posts.map((p) => {
        const firstMedia = Array.isArray(p.media) ? p.media[0] : null;
        const hasThumb = Boolean(mediaItemUrl(firstMedia));
        const isBusy = busyId === p.id;
        const when = p.scheduled_at || p.published_at || p.created_at;
        const contentType = p.content_type || 'post';
        const mediaCount = Array.isArray(p.media) ? p.media.length : 0;

        return (
          <li
            key={p.id}
            className="flex gap-3 text-sm border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 dark:border-white/15 dark:bg-gray-800/40"
          >
            {hasThumb && <GymSocialMediaThumb item={firstMedia} />}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${contentTypeBadgeClass(contentType)}`}
                >
                  {t(contentTypeI18nKey(contentType))}
                </span>
                <SocialTargetBadges targets={p.targets} />
                <span className={`font-medium ${gymSocialTitleText}`}>
                  {t(`gym.social.posts.status.${p.status}`)}
                </span>
                {contentType === 'post' && mediaCount > 1 && (
                  <span className={`text-[10px] ${gymSocialMutedText}`}>
                    {t('gym.social.posts.carouselBadge', { count: mediaCount })}
                  </span>
                )}
                {contentType === 'story' && mediaCount > 1 && (
                  <span className={`text-[10px] ${gymSocialMutedText}`}>
                    {t('gym.social.posts.storyBatchBadge', { count: mediaCount })}
                  </span>
                )}
                <span className={`text-xs ${gymSocialMutedText}`}>{formatWhen(when)}</span>
              </div>
              <p className={`truncate ${gymSocialMutedText}`}>{p.caption?.slice(0, 80) || '—'}</p>
              {p.status === 'scheduled' && p.scheduled_at && (
                <p className={`text-xs mt-0.5 flex items-center gap-1 ${gymSocialMutedText}`}>
                  <Clock className="h-3 w-3" />
                  {t('gym.social.posts.scheduledFor', { when: formatWhen(p.scheduled_at) })}
                </p>
              )}
              {p.publish_error && (
                <p className="text-xs text-red-600 dark:text-red-300 mt-1 truncate">{p.publish_error}</p>
              )}
              {['draft', 'scheduled', 'failed'].includes(p.status) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => onEditPost?.(p)}
                    className={`${gymSocialBtnSecondary} text-xs py-1 px-2`}
                  >
                    <Pencil className="h-3 w-3" />
                    {p.status === 'draft'
                      ? t('gym.social.posts.resumeDraft')
                      : t('gym.social.posts.editPost')}
                  </button>
                  {p.status === 'scheduled' && (
                    <>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => runAction('publish_now', { id: p.id }, 'gym.social.posts.published')}
                        className={`${gymSocialBtnPrimary} text-xs py-1 px-2`}
                      >
                        <Send className="h-3 w-3" />
                        {t('gym.social.posts.publishNow')}
                      </button>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => runAction('cancel_schedule', { id: p.id }, 'gym.social.posts.scheduleCancelled')}
                        className={`${gymSocialBtnSecondary} text-xs py-1 px-2`}
                      >
                        <XCircle className="h-3 w-3" />
                        {t('gym.social.posts.cancelSchedule')}
                      </button>
                    </>
                  )}
                  {['draft', 'scheduled', 'failed'].includes(p.status) && (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => runAction('delete', { id: p.id }, 'gym.social.posts.deleted')}
                      className="inline-flex items-center gap-1 text-xs py-1 px-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-400/50 dark:text-red-300 dark:hover:bg-red-900/20 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                      {t('gym.social.posts.delete')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
